import { AGUAppModuleDefinition } from './app-module-definition';

/**
 * The keep-alive seam for a composed AGU app surface.
 *
 * A surface used to render exactly one module at a time through
 * `ngComponentOutlet`, which DESTROYS the outgoing module's component. Leaving a
 * finder for another module and coming back therefore re-created it from
 * scratch: the query text, filters, sort, page and results were all gone, and
 * the module re-ran its on-mount load (live finding #19; suite J-6 requires
 * finder state to survive sidebar navigation).
 *
 * The fix is to mount each module ONCE, on its first visit, and thereafter hide
 * it rather than destroy it — component state is then preserved by the framework
 * for free, with no per-module state cache to keep in step with the components
 * that own it. Mounting stays LAZY (first visit, not surface open) so opening an
 * app still costs one module's load, not every module's.
 *
 * These are the two pure decisions that drives; the surface owns the rendering
 * and each module owns its own data.
 */

/**
 * The mounted set after visiting `module`: unchanged (same array reference, so a
 * `@for` re-render moves nothing) when the module is already mounted or there is
 * no module to mount, otherwise the set with `module` appended.
 */
export function WithMountedModule(
    mounted: readonly AGUAppModuleDefinition[],
    module: AGUAppModuleDefinition | null
): readonly AGUAppModuleDefinition[] {
    if (module === null) return mounted;
    if (mounted.some((m) => m.Key === module.Key)) return mounted;
    return [...mounted, module];
}

/**
 * True when navigation moves INTO `moduleKey` from somewhere else.
 *
 * A kept-alive module keeps whatever it loaded on mount, which is the point for
 * a finder's results but wrong for a board whose contents another module can
 * change (the Saved board, Home's counts and picks). Those modules re-load on
 * this signal — the same moment a destroy-and-recreate surface would have
 * re-run their `ngOnInit`. Movement WITHIN a module (list → detail → back) is
 * deliberately not re-entry: nothing left the module for anything to change.
 */
export function IsModuleReentry(moduleKey: string, previousModuleKey: string, nextModuleKey: string): boolean {
    return nextModuleKey === moduleKey && previousModuleKey !== moduleKey;
}

/**
 * Where a surface's host element stands relative to the document.
 * `never-attached` is distinct from `detached` so the FIRST attach — the shell
 * appends the wrapper after creating the component, which can be after
 * `ngOnInit` — is not mistaken for a member coming back.
 */
export type AGUSurfaceAttachment = 'never-attached' | 'attached' | 'detached';

/**
 * Pure transition for the re-entry signal: given the previous attachment and
 * whether the host is connected now, the next attachment and whether this is a
 * re-entry (detached → attached). Drives `BaseAGUAppSurfaceComponent
 * .OnSurfaceReentered`; kept here, beside `IsModuleReentry`, because it is the
 * same kind of decision one level up — the whole surface coming back rather
 * than one module inside it.
 */
export function NextSurfaceAttachment(
    previous: AGUSurfaceAttachment,
    isConnected: boolean
): { Attachment: AGUSurfaceAttachment; Reentered: boolean } {
    if (isConnected) return { Attachment: 'attached', Reentered: previous === 'detached' };
    return { Attachment: previous === 'never-attached' ? 'never-attached' : 'detached', Reentered: false };
}
